import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../lib/cn';
import { currentUser, logout } from '../lib/auth';
import { Button } from './ui';
import { applyTheme, getStoredTheme, type Theme } from '../lib/theme';

type NavItem = { to: string; label: string; icon: string };

// Espelha a navegação do SSR atual (app/view/frontend.js)
const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '▚' },
  { to: '/metrics', label: 'Metrics', icon: '∿' },
  { to: '/campaigns', label: 'Campaigns', icon: '◫' },
  { to: '/destinations', label: 'Destinations', icon: '⌖' },
  { to: '/reports', label: 'Reports', icon: '▤' },
  { to: '/clients', label: 'Clients', icon: '◕' },
  { to: '/finance', label: 'Finance', icon: '◈' },
  { to: '/billing', label: 'Billing', icon: '▦' },
  { to: '/fraud-guard', label: 'Fraud Guard', icon: '❖' },
  { to: '/implementation', label: 'Implementation', icon: '⟨⟩' },
  { to: '/debug', label: 'Debug', icon: '⚙' },
  { to: '/docs', label: 'API Docs', icon: '❏' },
  { to: '/settings', label: 'Settings', icon: '☰' },
];

// Cliente (não-admin) tem acesso bem limitado: campanhas, destinations e billing.
const CLIENT_NAV_PATHS = ['/campaigns', '/destinations', '/billing'];

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const isAdmin = !!currentUser()?.isAdmin;
  const items = isAdmin ? NAV : NAV.filter((i) => CLIENT_NAV_PATHS.includes(i.to));
  return (
    <aside
      className={cn(
        'w-60 shrink-0 bg-chrome-900 text-slate-300 flex flex-col',
        // No mobile e um drawer sobreposto; de lg pra cima volta a ser coluna fixa.
        'fixed inset-y-0 left-0 z-40 transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0',
        open ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
      )}
    >
      <div className="h-16 flex items-center gap-2 px-5 border-b border-white/5">
        <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-brand-500 to-fuchsia-500" />
        <span className="text-white font-semibold tracking-tight">PixelDMP</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-brand-500 font-bold">beta</span>
      </div>
      <nav className="flex-1 py-3 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-5 py-2.5 text-sm transition-colors',
                isActive
                  ? 'bg-white/5 text-white border-l-2 border-brand-500'
                  : 'border-l-2 border-transparent hover:bg-white/5 hover:text-white'
              )
            }
          >
            <span className="w-4 text-center opacity-70">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 text-[11px] text-slate-500 border-t border-white/5">
        New front-end · incremental migration
      </div>
    </aside>
  );
}


/** Alterna claro/escuro. O estado real vive na classe do <html>. */
function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      onClick={() => { applyTheme(next); setTheme(next); }}
      title={next === 'dark' ? 'Switch to dark theme' : 'Switch to light theme'}
      aria-label={next === 'dark' ? 'Switch to dark theme' : 'Switch to light theme'}
      className="w-8 h-8 rounded-lg border border-border text-slate-500 hover:text-ink-900 hover:bg-surface-muted transition-colors flex items-center justify-center text-sm"
    >
      {theme === 'dark' ? '☀' : '☽'}
    </button>
  );
}

export function Layout({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Fecha o drawer ao trocar de rota (inclusive em navegacao pelo historico,
  // que nao passa pelo onClick do NavLink).
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Trava o scroll do fundo enquanto o drawer esta aberto no mobile.
  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [navOpen]);

  return (
    <div className="flex h-full">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

      {/* Backdrop: so existe no mobile e so quando aberto. */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 bg-surface border-b border-border flex items-center px-4 sm:px-6 gap-3">
          <button
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            className="lg:hidden w-9 h-9 -ml-1 rounded-lg text-slate-600 hover:bg-surface-muted transition-colors flex items-center justify-center shrink-0"
          >
            <span className="text-lg leading-none">☰</span>
          </button>
          <h1 className="text-lg font-semibold text-ink-900 truncate">{title}</h1>
          <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
            {actions}
            <ThemeToggle />
            {(() => {
              const u = currentUser();
              return u ? (
                <div className="flex items-center gap-3 sm:pl-3 sm:border-l border-border">
                  {u.impersonated && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-medium" title="Você está logado como este usuário via impersonation. Log Out encerra e volta ao seu login.">
                      impersonating
                    </span>
                  )}
                  {/* O email ocupa espaco demais em tela pequena. */}
                  <span className="hidden md:inline text-sm text-slate-500">{u.email || 'user'}</span>
                  <Button variant="ghost" onClick={() => logout()}>Log Out</Button>
                </div>
              ) : null;
            })()}
          </div>
        </header>
        <main className="relative flex-1 overflow-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
